/**
 * 版面密度：'cozy'（寬鬆，預設）／'compact'（緊湊）。
 *
 * 只回一個 CSS class，實際樣式在 styles.css 的 `.cui-density--*` 底下 —— 不要在元件裡寫 inline style，
 * 否則主題、斷點和密度會互相打架。選擇存在 localStorage，下次開還是同一種密度。
 *
 * 用法：
 * ```tsx
 * const { density, toggle, className } = useDensity();
 * <div className={className}>…</div>
 * ```
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
export const DENSITY_STORAGE_KEY = 'console-ui.density.v1';
export const DEFAULT_DENSITY = 'cozy';
/** 掛在容器上的 class；`data-cui-density` 也吃這個名字，測試可直接斷言。 */
export function densityClassName(density) {
    return density === 'compact' ? 'cui-density--compact' : 'cui-density--cozy';
}
function normalizeDensity(value) {
    return value === 'cozy' || value === 'compact' ? value : null;
}
/** 讀存下來的密度；沒存過或壞掉 → null（由呼叫端決定預設，才能分辨「使用者選過」）。 */
export function readStoredDensity(storageKey = DENSITY_STORAGE_KEY) {
    if (typeof window === 'undefined' || !window.localStorage)
        return null;
    try {
        return normalizeDensity(window.localStorage.getItem(storageKey));
    }
    catch {
        return null;
    }
}
/**
 * 密度狀態。SSR／沒有 localStorage 時退回 `initial`（預設 cozy），不會在 hydration 前後閃一下。
 */
export function useDensity(options = {}) {
    const { initial = DEFAULT_DENSITY, storageKey = DENSITY_STORAGE_KEY } = options;
    const [density, setDensityState] = useState(() => readStoredDensity(storageKey) ?? initial);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.localStorage)
            return;
        try {
            window.localStorage.setItem(storageKey, density);
        }
        catch {
            /* 無痕／配額滿：記不住不影響使用 */
        }
    }, [density, storageKey]);
    const setDensity = useCallback((next) => {
        setDensityState(normalizeDensity(next) ?? DEFAULT_DENSITY);
    }, []);
    const toggle = useCallback(() => {
        setDensityState((prev) => (prev === 'compact' ? 'cozy' : 'compact'));
    }, []);
    const className = useMemo(() => densityClassName(density), [density]);
    return { density, isCompact: density === 'compact', setDensity, toggle, className };
}
//# sourceMappingURL=useDensity.js.map